import { HttpParams } from '@angular/common/http';
import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { DocumentoService } from '@app/core/services/documento.service';
import { Documento } from '@app/models/documento';
import { Usuario } from '@app/models/usuario';
import { ConfirmationService, MessageService } from 'primeng/api';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { environment } from 'src/environments/environment';
import { Subject, takeUntil } from 'rxjs';

@Component({
    selector: 'app-documentos-form',
    standalone: false,
    templateUrl: './documentos-form.component.html',
    styleUrl: './documentos-form.component.scss',
    providers: [ConfirmationService]
})
export class DocumentosFormComponent implements OnInit, OnDestroy {
    getTipo(id_tipo: number) {
        return this.tiposDocumento.find((t) => t.value == id_tipo)?.label || 'Nada que mostrar';
    }

    private readonly config = inject(DynamicDialogConfig);
    private readonly ref = inject(DynamicDialogRef);
    private readonly fb = inject(FormBuilder);
    private readonly messageService = inject(MessageService);
    form!: FormGroup;

    private readonly service = inject(DocumentoService);
    private readonly sanitizer = inject(DomSanitizer);
    private readonly confirmationService = inject(ConfirmationService);
    private readonly destroy$ = new Subject<void>();

    tiposDocumento = [
        { label: 'Credencial Manejo Defensivo', value: 1 },
        { label: 'DNI', value: 5 },
        { label: 'Lic Nac de Conducir', value: 6 }
    ];

    documentosVigentes: Documento[] = [];
    documentosProximos: Documento[] = [];
    documentosVencidos: Documento[] = [];

    /** Datos del conductor del usuario para pre-llenado */
    private conductorData: Usuario['conductor'] | null = null;

    /** Controla si mostrar el banner de sugerencia */
    mostrarSugerenciaLicencia = false;

    /** Preview del adjunto en mini-modal */
    adjuntoPreview: { url: string; safeUrl: SafeResourceUrl; isPdf: boolean; nombre: string } | null = null;

    nombre: string = '';

    ngOnInit() {
        const { conductor, ...rest }: Usuario = this.config.data?.usuario || {};

        this.nombre = `${rest.nombre ?? 'Sin nombre'}, ${rest.apellido ?? 'Sin apellido'}`;
        this.conductorData = conductor ?? null;

        // Limpiar listas antes de cargar (evita mezcla entre aperturas)
        this.documentosVigentes = [];
        this.documentosProximos = [];
        this.documentosVencidos = [];

        this.setForm();

        const params = new HttpParams().set('id_conductor', conductor?.id ?? '');
        this.service.findAll(params).pipe(takeUntil(this.destroy$)).subscribe((data) => {
            this.setListas(data);
        });
    }

    ngOnDestroy() {
        this.destroy$.next();
        this.destroy$.complete();
    }

    private setListas(data: Documento[]) {
        const hoy = new Date();
        const tresMesesDespues = new Date();
        tresMesesDespues.setMonth(hoy.getMonth() + 3);

        this.documentosVencidos = data.map((d) => ({ ...d, fecha_venc: new Date(d.fecha_venc) })).filter((d) => d.fecha_venc.getTime() < hoy.getTime());

        this.documentosProximos = data.map((d) => ({ ...d, fecha_venc: new Date(d.fecha_venc) })).filter((d) => d.fecha_venc.getTime() >= hoy.getTime() && d.fecha_venc.getTime() <= tresMesesDespues.getTime());

        this.documentosVigentes = data.map((d) => ({ ...d, fecha_venc: new Date(d.fecha_venc) })).filter((d) => d.fecha_venc.getTime() > tresMesesDespues.getTime());
    }

    verAdjunto(doc: Documento): void {
        if (doc.adjunto) {
            const url = environment.imagesUrl + 'documentacion/' + doc.adjunto;
            const isPdf = doc.adjunto.toLowerCase().endsWith('.pdf');
            this.adjuntoPreview = {
                url,
                safeUrl: this.sanitizer.bypassSecurityTrustResourceUrl(url),
                isPdf,
                nombre: this.getTipo(doc.id_tipo)
            };
        } else {
            this.messageService.add({ severity: 'warning', summary: 'Sin adjunto', detail: 'No existen adjuntos en este documento.' });
        }
    }

    cerrarPreview(): void {
        this.adjuntoPreview = null;
    }

    file!: File | null;
    //  Manejador para el evento de selección de archivo.
    onArchivoSeleccionado(event: any): void {
        const archivo: File = event.target.files?.[0];
        if (archivo) {
            this.file = archivo;
        }
    }

    // Si existe, devuelve el nombre del archivo, si no, devuelve una cadena vacía.
    getNombreArchivo(_: File): string {
        return this.file ? this.file.name : '';
    }

    agregarDocumento(): void {
        if (this.form.invalid) {
            this.form.markAllAsTouched();
            return;
        }

        const formData = new FormData();

        if (this.file) formData.append('file', this.file);

        const documentacion = {
            conductor: this.form.value.conductor,
            id_tipo: this.form.value.id_tipo.value,
            fecha_emision: this.form.value.fecha_emision,
            fecha_venc: this.form.value.fecha_venc
        };

        formData.append('form', JSON.stringify(documentacion));

        this.service.insert(formData).pipe(takeUntil(this.destroy$)).subscribe({
            next: (data) => {
                this.setListas(data);
                this.form.reset();
                this.file = null;
                this.setForm();
                this.messageService.add({ severity: 'warning', summary: 'Éxito', detail: 'Documento cargado con éxito.' });
            },
            error: (err) => {
                console.error(err);
                const status = err.status;
                switch (status) {
                    case 409:
                        this.messageService.add({ severity: 'warning', summary: 'Error', detail: 'El documento ya se encuentra vencido.' });
                        break;
                    default:
                        this.messageService.add({ severity: 'error', summary: 'Error', detail: 'No se pudieron cargar las opciones.' });
                        break;
                }
            }
        });
    }

    eliminarDocumento(doc: Documento, _: Documento[]): void {
        this.confirmationService.confirm({
            key: 'confirmEliminarDoc',
            header: 'Desactivar documento',
            message: `¿Estás seguro de que querés desactivar el documento <strong>${this.getTipo(doc.id_tipo)}</strong>? El registro no se eliminará, quedará inactivo.`,
            icon: 'pi pi-exclamation-triangle',
            acceptLabel: 'Sí, desactivar',
            rejectLabel: 'Cancelar',
            acceptButtonStyleClass: 'p-button-danger p-button-sm',
            rejectButtonStyleClass: 'p-button-text p-button-secondary p-button-sm',
            accept: () => {
                this.service.updateActivo(doc.id).pipe(takeUntil(this.destroy$)).subscribe({
                    next: (data) => {
                        this.setListas(data);
                        this.form.reset();
                        this.setForm();
                        this.messageService.add({ severity: 'success', summary: 'Éxito', detail: 'Documento desactivado correctamente.' });
                    },
                    error: (err) => {
                        console.error(err);
                        const status = err.status;
                        switch (status) {
                            case 409:
                                this.messageService.add({ severity: 'warning', summary: 'Error', detail: 'El documento ya se encuentra vencido.' });
                                break;
                            default:
                                this.messageService.add({ severity: 'error', summary: 'Error', detail: 'No se pudo desactivar el documento.' });
                                break;
                        }
                    }
                });
            }
        });
    }

    cerrar(): void {
        // Mandar los tres arrays combinados (o el que prefieras)
        const documentosActualizados = [...this.documentosVigentes, ...this.documentosProximos, ...this.documentosVencidos];

        this.ref.close(documentosActualizados);
    }

    private setForm() {
        // Obtenemos el usuario de la configuración, o un objeto vacío si no existe
        const usuario = this.config.data?.usuario || {};

        const conductorId = usuario.conductor?.id || null;

        this.form = this.fb.group({
            conductor: { id: conductorId },
            id_tipo: [null, Validators.required],
            fecha_emision: [null, Validators.required],
            fecha_venc: [null, Validators.required]
        });

        // Cuando seleccionan Lic Nac de Conducir (6), verificar si conviene pre-llenar
        this.form.get('id_tipo')?.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((tipo) => {
            if (tipo?.value === 6) {
                this.mostrarSugerenciaLicencia = this.verificarSugerenciaLicencia();
            } else {
                this.mostrarSugerenciaLicencia = false;
            }
        });
    }

    /** Devuelve true si: no hay ninguna Lic Nac de Conducir cargada Y el conductor tiene vencimiento_licencia */
    private verificarSugerenciaLicencia(): boolean {
        const tieneVencimiento = !!this.conductorData?.vencimiento_licencia;
        const yaExisteDoc = [...this.documentosVigentes, ...this.documentosProximos, ...this.documentosVencidos]
            .some((d) => d.id_tipo === 6);
        return tieneVencimiento && !yaExisteDoc;
    }

    /** Pre-llena fecha de vencimiento desde el conductor */
    aplicarDatosLicencia(): void {
        if (!this.conductorData?.vencimiento_licencia) return;
        this.form.patchValue({
            fecha_venc: new Date(this.conductorData.vencimiento_licencia)
        });
        this.mostrarSugerenciaLicencia = false;
    }

    descartarSugerencia(): void {
        this.mostrarSugerenciaLicencia = false;
    }
}
